import { useAuth } from '@contexts/Auth'
import markAsRead from '@services/api/notifications/markAsRead'
import useGetNotifications from '@services/react-query/hooks/user/queries/useGetNotifications'
import { Notification } from '@type/notifications'
import * as React from 'react'

type Total = {
  totalCount: number
  totalPages: number
  unreadCount: number
}

type State = {
  notifications: Notification[]
  isLoading: boolean
  isFetchingMore: boolean
  currentPage: number
  total: Total
  fetchNotifications: () => void
  fetchMoreNotifications: () => void
  markAllAsRead: () => Promise<void>
}

type NotificationsProps = { children: React.ReactNode }

const Notifications = React.createContext<State | undefined>(undefined)

const NotificationsProvider = ({ children }: NotificationsProps) => {
  const { user } = useAuth()
  const { data, isLoading, refetch, fetchNextPage, isFetchingNextPage } =
    useGetNotifications(10, user?._id)

  const markAllAsRead = async () => {
    if (!user?._id) return
    try {
      if (data?.pages[0]?.unreadCount && data.pages[0].unreadCount > 0) {
        await markAsRead(user._id)
        refetch()
      }
    } catch (error) {}
  }

  const currentPage = data?.pages?.length || 1

  const notifications = React.useMemo(
    () => data?.pages?.flatMap((page) => page.notifications) || [],
    [data]
  )

  const total = React.useMemo(
    () => ({
      totalCount: data?.pages?.[0]?.totalCount || 0,
      totalPages: data?.pages?.[0]?.totalPages || 0,
      unreadCount: data?.pages?.[0]?.unreadCount || 0,
    }),
    [data]
  )

  const state: State = {
    fetchMoreNotifications: fetchNextPage,
    fetchNotifications: refetch,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    notifications,
    total,
    currentPage,
    markAllAsRead,
  }

  return (
    <Notifications.Provider value={state}>{children}</Notifications.Provider>
  )
}

const useNotifications = () => {
  const context = React.useContext(Notifications)
  if (context === undefined) {
    throw new Error(
      'useNotificationsState must be used within an Notifications'
    )
  }
  return context
}

export { NotificationsProvider, useNotifications }
